import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ConfirmHost } from '@/components/ConfirmDialog';
import { AuthProvider, useAuth } from '@/lib/auth';
import Layout from '@/components/Layout';
import ScrollToTop from '@/components/ScrollToTop';
import Login from '@/pages/Login';
// Route-level code splitting: each page is its own chunk so first load only ships the app shell +
// the requested page (not all 39 Coach OS pages). Login stays eager for instant first paint.
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const DataReview = lazy(() => import('@/pages/DataReview'));
const People = lazy(() => import('@/pages/People'));
const PersonDetail = lazy(() => import('@/pages/PersonDetail'));
const Companies = lazy(() => import('@/pages/Companies'));
const CompanyDetail = lazy(() => import('@/pages/CompanyDetail'));
const Pipeline = lazy(() => import('@/pages/Pipeline'));
const Tasks = lazy(() => import('@/pages/Tasks'));
const Knowledge = lazy(() => import('@/pages/Knowledge'));
const Content = lazy(() => import('@/pages/Content'));
const BusinessBrain = lazy(() => import('@/pages/BusinessBrain'));
const Calendar = lazy(() => import('@/pages/Calendar'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const BusinessDNA = lazy(() => import('@/pages/BusinessDNA'));
const Coaching = lazy(() => import('@/pages/Coaching'));
const CoachingClient = lazy(() => import('@/pages/CoachingClient'));
const CoachTraining = lazy(() => import('@/pages/CoachTraining'));
const IntelligenceDashboard = lazy(() => import('@/pages/IntelligenceDashboard'));
const GlobalCheckins = lazy(() => import('@/pages/GlobalCheckins'));
const WorkoutLibrary = lazy(() => import('@/pages/WorkoutLibrary'));
const CustomExercises = lazy(() => import('@/pages/CustomExercises'));
const ResourceLibrary = lazy(() => import('@/pages/ResourceLibrary'));
const VerifiedFoods = lazy(() => import('@/pages/VerifiedFoods'));
const CoachInbox = lazy(() => import('@/pages/CoachInbox'));
const Corporate = lazy(() => import('@/pages/Corporate'));
const CorporateProgramme = lazy(() => import('@/pages/CorporateProgramme'));
const ProspectDiscovery = lazy(() => import('@/pages/ProspectDiscovery'));
const Supplements = lazy(() => import('@/pages/Supplements'));
const Profile = lazy(() => import('@/pages/Profile'));
const Training = lazy(() => import('@/pages/Training'));
const AICreator = lazy(() => import('@/pages/AICreator'));
const Settings = lazy(() => import('@/pages/Settings'));
const AutomationSettings = lazy(() => import('@/pages/AutomationSettings'));
const Help = lazy(() => import('@/pages/Help'));
const PublicReport = lazy(() => import('@/pages/PublicReport'));
const PublicCorporateReport = lazy(() => import('@/pages/PublicCorporateReport'));
const CorporateOnboard = lazy(() => import('@/pages/CorporateOnboard'));
const ClientPortal = lazy(() => import('@/pages/portal/ClientPortal'));
const RehabAdmin = lazy(() => import('@/pages/rehab/RehabAdmin'));
const PractitionerApp = lazy(() => import('@/pages/rehab/practitioner/PractitionerApp'));
import '@/App.css';

// Lightweight in-shell skeleton — shown while a page chunk loads. Intentionally minimal (no full-page
// spinner) so the sidebar/header stay put and there's no jarring flash between routes.
function PageSkeleton() {
  return (
    <div className="p-6 animate-pulse space-y-4" aria-busy="true" aria-label="Loading page">
      <div className="h-8 w-56 bg-muted rounded" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-28 bg-muted rounded-lg" />)}
      </div>
    </div>
  );
}

function ManifestSwitcher() {
  const location = useLocation();
  React.useEffect(() => {
    const coach = !location.pathname.startsWith('/my');
    const m = document.querySelector('link[rel="manifest"]');
    if (m) m.setAttribute('href', coach ? '/manifest-coach.json' : '/manifest.json');
    const t = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (t) t.setAttribute('content', coach ? 'STP OS' : 'STP Coach');
    document.title = location.pathname.startsWith('/practitioner') ? 'STP Sports Rehab' : (coach ? 'Set The Pace OS' : 'STP Coach');
  }, [location.pathname]);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  // Route-guard contract for Coach OS routes (verified end-to-end):
  //  • Valid / loading / transient coach session → wait, then remain in Coach OS (never downgrade to /my).
  //  • No coach session (no/expired coach token) → send to the COACH login /login so ANY coach — including
  //    dual-role users who still have a lingering client token — can always re-authenticate as coach.
  //    We must NOT auto-downgrade a Coach OS route to /my just because a client token exists: that traps a
  //    logged-out coach in Client OS with no way back to the coach login. Client-only users are unaffected —
  //    they use the separate /my client portal (its own router + guard) and never rely on this branch.
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout><Suspense fallback={<PageSkeleton />}>{children}</Suspense></Layout>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <ManifestSwitcher />
        <ConfirmHost />
        <Toaster position="top-right" offset={{ top: 'calc(env(safe-area-inset-top) + 16px)' }} mobileOffset={{ top: 'calc(env(safe-area-inset-top) + 68px)' }} toastOptions={{ style: { borderRadius: '4px', border: '1px solid hsl(240 6% 90%)' } }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<Suspense fallback={null}><ResetPassword /></Suspense>} />
          <Route path="/my/*" element={<Suspense fallback={null}><ClientPortal /></Suspense>} />
          <Route path="/practitioner/*" element={<Suspense fallback={null}><PractitionerApp /></Suspense>} />
          <Route path="/rehab/*" element={<Protected><RehabAdmin /></Protected>} />
          <Route path="/r/:token" element={<Suspense fallback={null}><PublicReport /></Suspense>} />
          <Route path="/cr/:token" element={<Suspense fallback={null}><PublicCorporateReport /></Suspense>} />
          <Route path="/join/:token" element={<Suspense fallback={null}><CorporateOnboard /></Suspense>} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/people" element={<Protected><People /></Protected>} />
          <Route path="/people/:id" element={<Protected><PersonDetail /></Protected>} />
          <Route path="/companies" element={<Protected><Companies /></Protected>} />
          <Route path="/companies/:id" element={<Protected><CompanyDetail /></Protected>} />
          <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
          <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
          <Route path="/supplements" element={<Protected><Supplements /></Protected>} />
          <Route path="/knowledge" element={<Protected><Knowledge /></Protected>} />
          <Route path="/content" element={<Protected><Content /></Protected>} />
          <Route path="/brain" element={<Protected><BusinessBrain /></Protected>} />
          <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
          <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
          <Route path="/dna" element={<Protected><BusinessDNA /></Protected>} />
          <Route path="/coaching" element={<Protected><Coaching /></Protected>} />
          <Route path="/coaching/intelligence" element={<Protected><IntelligenceDashboard /></Protected>} />
          <Route path="/coaching/checkins" element={<Protected><GlobalCheckins /></Protected>} />
          <Route path="/coaching/data-review" element={<Protected><DataReview /></Protected>} />
          <Route path="/coaching/:id" element={<Protected><CoachingClient /></Protected>} />
          <Route path="/coaching/:id/training" element={<Protected><CoachTraining /></Protected>} />
          <Route path="/workout-library" element={<Protected><WorkoutLibrary /></Protected>} />
          <Route path="/exercise-library" element={<Protected><CustomExercises /></Protected>} />
          <Route path="/resources" element={<Protected><ResourceLibrary /></Protected>} />
          <Route path="/verified-foods" element={<Protected><VerifiedFoods /></Protected>} />
          <Route path="/inbox" element={<Protected><CoachInbox /></Protected>} />
          <Route path="/corporate" element={<Protected><Corporate /></Protected>} />
          <Route path="/corporate/:id" element={<Protected><CorporateProgramme /></Protected>} />
          <Route path="/prospect-discovery" element={<Protected><ProspectDiscovery /></Protected>} />
          <Route path="/training" element={<Protected><Training /></Protected>} />
          <Route path="/create" element={<Protected><AICreator /></Protected>} />
          <Route path="/create/:creatorId" element={<Protected><AICreator /></Protected>} />
          <Route path="/rewrite" element={<Navigate to="/create/rewrite" replace />} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/settings/automations" element={<Protected><AutomationSettings /></Protected>} />
          <Route path="/help" element={<Protected><Help /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
